import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Users, Search, Filter, ChevronDown, Eye, CheckCircle, XCircle,
  Clock, AlertCircle, FileText, Phone, Mail, MapPin, Award,
  ExternalLink, Upload, Shield, X, User
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700' },
  pending_documents: { label: 'Pending Docs', color: 'bg-orange-100 text-orange-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
};

const CERT_STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-gray-400' },
  pending: { label: 'Pending', color: 'text-yellow-600' },
  uploaded: { label: 'Uploaded', color: 'text-blue-600' },
  verified: { label: 'Verified', color: 'text-green-600' },
  expired: { label: 'Expired', color: 'text-red-600' }
};

const AdminCoachApplicationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [teams, setTeams] = useState([]);
  const [ymcaUrl, setYmcaUrl] = useState('');
  
  // Action states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchTeams();
  }, [filterStatus]);

  const fetchApplications = async () => {
    try {
      let url = `${API_URL}/api/coach-applications`;
      if (filterStatus) {
        url += `?status=${filterStatus}`;
      }
      const res = await axios.get(url, { withCredentials: true });
      setApplications(res.data.applications);
      setCounts(res.data.counts);
      setYmcaUrl(res.data.ymca_background_check_url);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teams`, { withCredentials: true });
      setTeams(res.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    setActionLoading(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'rejected' && rejectionReason) {
        payload.rejection_reason = rejectionReason;
      }
      if (adminNotes) {
        payload.admin_notes = adminNotes;
      }
      if (selectedTeam) {
        payload.assigned_team_id = selectedTeam;
      }
      
      await axios.patch(`${API_URL}/api/coach-applications/${appId}`, payload, {
        withCredentials: true
      });
      
      fetchApplications();
      setSelectedApp(null);
      setShowRejectModal(false);
      setRejectionReason('');
      setAdminNotes('');
      setSelectedTeam('');
    } catch (error) {
      console.error('Failed to update application:', error);
      alert(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyCertification = async (appId, certType) => {
    try {
      await axios.patch(
        `${API_URL}/api/coach-applications/${appId}/certifications/${certType}/verify`,
        {},
        { withCredentials: true }
      );
      // Refresh the selected application
      const res = await axios.get(`${API_URL}/api/coach-applications/${appId}`, { withCredentials: true });
      setSelectedApp(res.data.application);
    } catch (error) {
      console.error('Failed to verify certification:', error);
      alert('Failed to verify certification');
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      app.first_name?.toLowerCase().includes(search) ||
      app.last_name?.toLowerCase().includes(search) ||
      app.email?.toLowerCase().includes(search)
    );
  });

  const ApplicationDetailModal = () => {
    if (!selectedApp) return null;
    
    const certs = selectedApp.certifications || {};
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-4">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-navy">
                {selectedApp.first_name} {selectedApp.last_name}
              </h2>
              <Badge className={STATUS_CONFIG[selectedApp.status]?.color}>
                {STATUS_CONFIG[selectedApp.status]?.label}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedApp(null)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {/* Contact Info */}
            <div>
              <h3 className="font-semibold text-navy mb-3 flex items-center">
                <User className="w-4 h-4 mr-2" />
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <a href={`mailto:${selectedApp.email}`} className="text-navy hover:underline">
                    {selectedApp.email}
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-gray-400 mr-2" />
                  <a href={`tel:${selectedApp.phone}`} className="text-navy hover:underline">
                    {selectedApp.phone}
                  </a>
                </div>
                {selectedApp.address && (
                  <div className="flex items-start col-span-2">
                    <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                    <span>
                      {selectedApp.address}, {selectedApp.city}, {selectedApp.state} {selectedApp.zip_code}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Experience */}
            <div>
              <h3 className="font-semibold text-navy mb-3 flex items-center">
                <Award className="w-4 h-4 mr-2" />
                Coaching Experience
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                <p><strong>Years:</strong> {selectedApp.years_experience || 0}</p>
                <p><strong>Background:</strong> {selectedApp.coaching_experience || 'Not provided'}</p>
                <p><strong>Previous Teams:</strong> {selectedApp.previous_teams || 'Not provided'}</p>
              </div>
            </div>

            {/* Preferences */}
            <div>
              <h3 className="font-semibold text-navy mb-3">Preferences</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Preferred Role:</strong> {selectedApp.preferred_role?.replace('_', ' ')}</p>
                <div>
                  <strong>Preferred Divisions:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedApp.preferred_divisions?.map(d => (
                      <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                    ))}
                  </div>
                </div>
                {selectedApp.availability_notes && (
                  <p><strong>Availability:</strong> {selectedApp.availability_notes}</p>
                )}
              </div>
            </div>

            {/* References */}
            <div>
              <h3 className="font-semibold text-navy mb-3">References</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium">{selectedApp.reference1_name || 'Not provided'}</p>
                  <p className="text-gray-500">{selectedApp.reference1_relationship}</p>
                  <p>{selectedApp.reference1_phone}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium">{selectedApp.reference2_name || 'Not provided'}</p>
                  <p className="text-gray-500">{selectedApp.reference2_relationship}</p>
                  <p>{selectedApp.reference2_phone}</p>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="font-semibold text-navy mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Certifications
              </h3>
              <div className="space-y-3">
                {/* Background Check */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Background Check (YMCA)</p>
                    <p className={`text-sm ${CERT_STATUS_CONFIG[certs.background_check?.status]?.color}`}>
                      {CERT_STATUS_CONFIG[certs.background_check?.status]?.label}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <a 
                      href={ymcaUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-navy hover:underline flex items-center"
                    >
                      YMCA Portal <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                    {certs.background_check?.status !== 'verified' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVerifyCertification(selectedApp.application_id, 'background_check')}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>

                {/* USA Football */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">USA Football Certification</p>
                    <p className={`text-sm ${CERT_STATUS_CONFIG[certs.usa_football?.status]?.color}`}>
                      {CERT_STATUS_CONFIG[certs.usa_football?.status]?.label}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {certs.usa_football?.url && (
                      <a 
                        href={certs.usa_football.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-navy hover:underline"
                      >
                        View
                      </a>
                    )}
                    {certs.usa_football?.status === 'uploaded' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVerifyCertification(selectedApp.application_id, 'usa_football')}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>

                {/* CPR/First Aid */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">CPR/First Aid</p>
                    <p className={`text-sm ${CERT_STATUS_CONFIG[certs.cpr_first_aid?.status]?.color}`}>
                      {CERT_STATUS_CONFIG[certs.cpr_first_aid?.status]?.label}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {certs.cpr_first_aid?.url && (
                      <a 
                        href={certs.cpr_first_aid.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-navy hover:underline"
                      >
                        View
                      </a>
                    )}
                    {certs.cpr_first_aid?.status === 'uploaded' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVerifyCertification(selectedApp.application_id, 'cpr_first_aid')}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>

                {/* Concussion Training */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Concussion Training</p>
                    <p className={`text-sm ${CERT_STATUS_CONFIG[certs.concussion_training?.status]?.color}`}>
                      {CERT_STATUS_CONFIG[certs.concussion_training?.status]?.label}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {certs.concussion_training?.url && (
                      <a 
                        href={certs.concussion_training.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-navy hover:underline"
                      >
                        View
                      </a>
                    )}
                    {certs.concussion_training?.status === 'uploaded' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVerifyCertification(selectedApp.application_id, 'concussion_training')}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <h3 className="font-semibold text-navy mb-3">Admin Notes</h3>
              <Textarea
                value={adminNotes || selectedApp.admin_notes || ''}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this application..."
                rows={3}
              />
            </div>

            {/* Team Assignment (for approval) */}
            {selectedApp.status !== 'approved' && selectedApp.status !== 'rejected' && (
              <div>
                <h3 className="font-semibold text-navy mb-3">Team Assignment</h3>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Assign later...</option>
                  {teams.map(team => (
                    <option key={team.team_id} value={team.team_id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Actions */}
            {selectedApp.status !== 'approved' && selectedApp.status !== 'rejected' && (
              <div className="flex space-x-3 pt-4 border-t">
                {selectedApp.status === 'submitted' && (
                  <Button 
                    onClick={() => handleStatusChange(selectedApp.application_id, 'under_review')}
                    disabled={actionLoading}
                    className="bg-yellow-500 hover:bg-yellow-600"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Mark Under Review
                  </Button>
                )}
                
                <Button 
                  onClick={() => handleStatusChange(selectedApp.application_id, 'pending_documents')}
                  disabled={actionLoading}
                  variant="outline"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Request Documents
                </Button>
                
                <Button 
                  onClick={() => handleStatusChange(selectedApp.application_id, 'approved')}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                
                <Button 
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  variant="outline"
                  className="text-red border-red hover:bg-red hover:text-white"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const RejectModal = () => {
    if (!showRejectModal || !selectedApp) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <h3 className="text-lg font-bold text-red mb-4">Reject Application</h3>
          <p className="text-gray-600 mb-4">
            Please provide a reason for rejecting this application:
          </p>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={4}
            className="mb-4"
          />
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleStatusChange(selectedApp.application_id, 'rejected')}
              disabled={actionLoading || !rejectionReason.trim()}
              className="flex-1 bg-red hover:bg-red/90"
            >
              Confirm Rejection
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-coach-applications">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy">Coach Applications</h1>
            <p className="text-gray-600">Review and manage coaching staff applications</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${filterStatus === 'submitted' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'submitted' ? '' : 'submitted')}
          >
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{counts.submitted || 0}</p>
              <p className="text-sm text-gray-600">New</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${filterStatus === 'under_review' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'under_review' ? '' : 'under_review')}
          >
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{counts.under_review || 0}</p>
              <p className="text-sm text-gray-600">Reviewing</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${filterStatus === 'pending_documents' ? 'ring-2 ring-orange-500' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'pending_documents' ? '' : 'pending_documents')}
          >
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{counts.pending_documents || 0}</p>
              <p className="text-sm text-gray-600">Pending Docs</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${filterStatus === 'approved' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'approved' ? '' : 'approved')}
          >
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-green-600">{counts.approved || 0}</p>
              <p className="text-sm text-gray-600">Approved</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${filterStatus === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'rejected' ? '' : 'rejected')}
          >
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-red">{counts.rejected || 0}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10"
          />
        </div>

        {/* Applications List */}
        <Card>
          <CardContent className="p-0">
            {filteredApplications.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No applications found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredApplications.map(app => (
                  <div 
                    key={app.application_id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedApp(app)}
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center">
                          <User className="w-6 h-6 text-navy" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-navy">
                            {app.first_name} {app.last_name}
                          </h3>
                          <p className="text-sm text-gray-500">{app.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {app.preferred_role?.replace('_', ' ')}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              {app.years_experience || 0} yrs exp
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={STATUS_CONFIG[app.status]?.color}>
                          {STATUS_CONFIG[app.status]?.label}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">
                          {format(new Date(app.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <ApplicationDetailModal />
        <RejectModal />
      </div>
    </ParentLayout>
  );
};

export default AdminCoachApplicationsPage;
